import { Alert, App, Button, Popconfirm } from 'antd'
import { observer } from 'mobx-react-lite'
import { useDropzone } from 'react-dropzone'

import { userManager } from '@/Core/Managers'
import { Form, FormField } from '@/Core/UI/Form'
import { CurrentUserAvatar } from '@/Ship/Components/CurrentUser'
import { classNames } from '@/Ship/Helpers/classNames'

import { PHOTO_ACCEPT, PHOTO_MAX_MB, PROFILE_FIELDS, PROFILE_TEXTS } from '../../Contracts/IProfile'

import type { ProfileManager } from '../../Stores/ProfileManager'

import styles from './UserProfile.module.css'

/** Підписи й плейсхолдери — тексти легасі, з виправленими «Ім'я» й «По батькові» (ВП-9). */
const FIELDS = {
    firstName: { label: "Ім'я", isRequired: true, inputProps: { placeholder: "Ім'я" } },
    lastName: { label: 'Прізвище', isRequired: true, inputProps: { placeholder: 'Прізвище' } },
    middleName: { label: 'По батькові', inputProps: { placeholder: 'По батькові' } },
    email: { label: 'E-mail', isRequired: true, inputProps: { placeholder: 'E-mail', type: 'email' } },
    position: { label: 'Посада', inputProps: { placeholder: 'Посада' } },
}

const initialValues = (): Record<string, string> => {
    const user = userManager.user
    const values: Record<string, string> = {}

    for (const field of PROFILE_FIELDS) {
        const value = user[field]

        values[field] = typeof value === 'string' ? value : ''
    }

    return values
}

/**
 * Вкладка «Персональна інформація»: фото й особисті дані.
 *
 * Фото змінюється одразу, без «Зберегти зміни»: файл завантажується й тут-таки
 * прикріплюється до людини, а шапка показує його без перезавантаження.
 * «Видалити фото» — з підтвердженням: повернути можна лише новим
 * завантаженням.
 */
export const PersonalInfo = observer(({ manager }: { manager: ProfileManager }) => {
    const { notification } = App.useApp()
    const hasPhoto = Boolean((userManager.user['photo'] as { url?: unknown } | null | undefined)?.url)

    const { getRootProps, getInputProps } = useDropzone({
        accept: PHOTO_ACCEPT,
        maxSize: PHOTO_MAX_MB * 1024 * 1024,
        multiple: false,
        disabled: manager.isPhotoBusy,
        onDropAccepted: (files) => {
            const file = files[0]

            if (file) void manager.uploadPhoto(file)
        },
        onDropRejected: () => manager.rejectPhoto(PROFILE_TEXTS.photoRejected),
    })

    const onSubmit = async ({ values }: { values: Record<string, unknown> }): Promise<void> => {
        if (await manager.saveInfo(values)) {
            notification.success({ title: PROFILE_TEXTS.success, description: PROFILE_TEXTS.infoSaved })
        }
    }

    return (
        <>
            <div className={classNames(styles['photo'])}>
                <CurrentUserAvatar size={64} />

                <div className={classNames(styles['photoContent'])}>
                    <div className={classNames(styles['photoButtons'])}>
                        <div {...getRootProps()}>
                            <input {...getInputProps()} aria-label="Фото профілю" />
                            <Button type="primary" loading={manager.isPhotoBusy}>
                                Завантажити
                            </Button>
                        </div>

                        <Popconfirm
                            title="Видалити фото?"
                            okText="Так"
                            cancelText="Ні"
                            disabled={!hasPhoto || manager.isPhotoBusy}
                            onConfirm={() => manager.removePhoto()}
                        >
                            <Button danger disabled={!hasPhoto || manager.isPhotoBusy}>
                                Видалити фото
                            </Button>
                        </Popconfirm>
                    </div>
                    <p className={classNames(styles['photoHint'])}>{PROFILE_TEXTS.photoHint}</p>
                </div>
            </div>

            {manager.photoError ? (
                <Alert className={classNames(styles['alert'])} type="error" showIcon title={manager.photoError} />
            ) : null}

            <Form fields={FIELDS} initialValues={initialValues()} onSubmit={onSubmit}>
                <div className={classNames(styles['grid'])}>
                    {PROFILE_FIELDS.map((field) => (
                        <FormField key={field} name={field} />
                    ))}
                </div>

                {manager.infoError ? (
                    <Alert className={classNames(styles['alert'])} type="error" showIcon title={manager.infoError} />
                ) : null}

                <div className={classNames(styles['buttons'])}>
                    <Button type="primary" htmlType="submit" loading={manager.isInfoSaving}>
                        Зберегти зміни
                    </Button>
                </div>
            </Form>
        </>
    )
})
